import React from "react";
import PropTypes from "prop-types";
import { ButtonComponent } from "@syncfusion/ej2-react-buttons";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import {
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  VARIANT_CONFIG,
  SIZE_CONFIG,
} from "@/components/buttons/constants/buttonVariants";

/**
 * Button — Syncfusion-based button with project-standard variants and sizes.
 *
 * Uses `var(--primary-color, #4f47e6)` for the primary variant so it stays in
 * sync with the global theme defined in `variables.css`.
 *
 * The loading spinner is shown **alongside** the label (not replacing it),
 * so the user always knows which action is in progress. When loading, the
 * spinner replaces the icon slot; when not loading, the icon renders normally.
 *
 * @example
 * <Button label="Save" onClick={handleSave} />
 *
 * @example
 * <Button label="Delete" variant="danger" size="sm" />
 *
 * @example
 * <Button type="submit" form="my-form" label="Save" variant="primary" isLoading={isSaving} />
 *
 * @example
 * <Button label="Add" icon={<PlusIcon className="size-4" />} variant="success" />
 */
export default function Button({
  variant = BUTTON_VARIANTS.PRIMARY,
  size = BUTTON_SIZES.MD,
  label,
  icon,
  isLoading = false,
  isDisabled = false,
  className = "",
  children,
  ...props
}) {
  const variantCfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG[BUTTON_VARIANTS.DEFAULT];
  const sizeCfg = SIZE_CONFIG[size] || SIZE_CONFIG[BUTTON_SIZES.MD];

  const cssClass = [variantCfg.cssClass, sizeCfg.cssClass, className].filter(Boolean).join(" ");

  const isInactive = isLoading || isDisabled;

  const mergedStyle = {
    ...variantCfg.style,
    ...(sizeCfg.style || {}),
    ...(isInactive ? { opacity: 0.65, cursor: "not-allowed" } : {}),
  };

  return (
    <ButtonComponent
      cssClass={cssClass}
      disabled={isInactive}
      style={mergedStyle}
      {...props}
    >
      <span className="flex items-center justify-center gap-2">
        {isLoading ? (
          <ArrowPathIcon className="size-4 animate-spin" aria-hidden="true" />
        ) : (
          icon && <span className="flex shrink-0 items-center">{icon}</span>
        )}
        {label && <span>{label}</span>}
        {children}
      </span>
    </ButtonComponent>
  );
}

Button.propTypes = {
  /** Visual style variant */
  variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
  /** Size preset */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /** Button text label — always visible, even when loading */
  label: PropTypes.any,
  /** Optional leading icon (ReactNode); replaced by spinner when isLoading */
  icon: PropTypes.node,
  /** Show spinner alongside the label and disable the button */
  isLoading: PropTypes.bool,
  /** Disable the button without showing a spinner */
  isDisabled: PropTypes.bool,
  /** Additional CSS class names appended to Syncfusion's cssClass */
  className: PropTypes.string,
  /** Optional children rendered after the label */
  children: PropTypes.node,
};
